import { FilterBar, FilterRow } from "@components/FilterBar";
import { type CommunicationEventRow, useCommunicationEvents } from "@features/communications/api";
import { PageLayoutWithHelp } from "@features/page-help";
import EnterpriseTable from "@shared/ui/enterprise-table";
import { Alert, DatePicker, InputNumber, Space, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { type Dayjs } from "dayjs";
import { useMemo, useState } from "react";

const { RangePicker } = DatePicker;

const CommunicationEventsPage = () => {
  const [controller, setController] = useState<number | undefined>();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().subtract(7, "day").startOf("day"),
    dayjs(),
  ]);

  const eventsQuery = useCommunicationEvents({
    controller,
    from: range[0].toISOString(),
    to: range[1].toISOString(),
    limit: 500,
  });

  const data = useMemo(() => eventsQuery.data ?? [], [eventsQuery.data]);

  const columns: ColumnsType<CommunicationEventRow> = [
    {
      title: "УСПД / канал",
      dataIndex: "controller_id",
      width: 160,
      render: (_value, row) => row.controller_name ?? row.controller_id,
    },
    {
      title: "Время события",
      dataIndex: "event_time",
      width: 200,
      render: (value: string) => dayjs(value).format("DD.MM.YYYY HH:mm:ss"),
    },
    {
      title: "Код",
      dataIndex: "event_code",
      width: 120,
    },
    {
      title: "Описание",
      dataIndex: "message",
      render: (_value, row) => row.message || row.event_code_text || "—",
    },
  ];

  return (
    <PageLayoutWithHelp title="Журнал событий УСПД">
      <Space orientation="vertical" size="large" style={{ width: "100%" }}>
        <Typography.Text type="secondary">
          Последние события по опросам и состоянию УСПД: пропадание связи, коррекция времени,
          восстановление опроса и др.
        </Typography.Text>
        <FilterBar>
          <FilterRow>
            <InputNumber
              value={controller}
              onChange={(value) => setController(value ?? undefined)}
              placeholder="ID УСПД"
              style={{ width: 160 }}
            />
            <RangePicker
              value={range}
              showTime
              onChange={(value) => {
                if (value?.[0] && value[1]) {
                  setRange([value[0], value[1]]);
                }
              }}
            />
          </FilterRow>
        </FilterBar>

        {!eventsQuery.isLoading && data.length === 0 ? (
          <Alert type="info" showIcon title="События отсутствуют" />
        ) : (
          <EnterpriseTable<CommunicationEventRow>
            rowKey={(row) => `${row.controller_id}-${row.event_time}-${row.event_code}`}
            columns={columns}
            dataSource={data}
            loading={eventsQuery.isLoading}
            scroll={{ x: 900 }}
            size="small"
            toolbar={{ reload: true, onReload: () => eventsQuery.refetch() }}
          />
        )}
      </Space>
    </PageLayoutWithHelp>
  );
};

export default CommunicationEventsPage;
